import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, Injector, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs';
import { AuthService } from './auth.service';
import {
  buildVaultAccountBlockedUserMessage,
  isVaultLoginRoute,
  isVaultAccountBlockedCircuitOpen,
  openVaultAccountBlockedCircuit,
  VAULT_LOGIN_BLOCKED_MESSAGE_KEY,
  persistVaultLoginBlockedMessage,
  resolveVaultAccountBlockedState,
  type VaultAccountBlockedState,
} from './vault-account-blocked.util';

@Injectable({ providedIn: 'root' })
export class VaultAccountBlockedService {
  private readonly http = inject(HttpClient);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private checkAccessInFlight: Observable<void> | null = null;

  /** @deprecated Solo compatibilidad; el bloqueo redirige al login en lugar de pantalla modal. */
  readonly state = signal<VaultAccountBlockedState | null>(null);

  applyFromError(err: unknown): boolean {
    const blocked = resolveVaultAccountBlockedState(err);
    if (!blocked) return false;
    if (isVaultLoginRoute()) return true;
    if (isVaultAccountBlockedCircuitOpen()) return true;
    this.rejectBlockedAccount(blocked);
    return true;
  }

  /** Limpia sesión y deja toast en login; no redirige (ya estamos en /login). */
  rejectBlockedAccountAtLogin(blocked: VaultAccountBlockedState): void {
    openVaultAccountBlockedCircuit();
    persistVaultLoginBlockedMessage(blocked);
    this.clear();
    this.injector.get(AuthService).clearAllData();
  }

  rejectBlockedAccount(blocked: VaultAccountBlockedState): void {
    openVaultAccountBlockedCircuit();
    persistVaultLoginBlockedMessage(blocked);
    this.clear();
    // Resolución lazy: evita NG0200 en el arranque (AuthService ↔ este servicio).
    this.injector.get(AuthService).clearAllData();
    if (!isVaultLoginRoute()) {
      void this.router.navigateByUrl('/login');
    }
  }

  setBlocked(message: string, reason: VaultAccountBlockedState['reason'] = 'unknown'): void {
    this.rejectBlockedAccount({ message, reason });
  }

  clear(): void {
    this.state.set(null);
  }

  /** Valida acceso Vault tras OAuth; falla con estado amigable si auth/me responde 403 de cuenta bloqueada. */
  checkAccountAccess$(): Observable<void> {
    if (typeof localStorage === 'undefined') return of(void 0);
    if (isVaultAccountBlockedCircuitOpen()) {
      return throwError(() => this.blockedFallbackState());
    }
    if (this.checkAccessInFlight) return this.checkAccessInFlight;

    const token = localStorage.getItem('access_token')?.trim();
    if (!token) return of(void 0);

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    this.checkAccessInFlight = this.http.get<unknown>('/api/auth/me', { headers }).pipe(
      map(() => void 0),
      catchError((err: unknown) => {
        const status =
          err instanceof HttpErrorResponse
            ? err.status
            : typeof (err as { status?: number })?.status === 'number'
              ? (err as { status: number }).status
              : 0;
        if (status === 403) {
          const blocked =
            resolveVaultAccountBlockedState(err) ?? this.blockedFallbackState();
          return throwError(() => blocked);
        }
        return of(void 0);
      }),
      finalize(() => {
        this.checkAccessInFlight = null;
      }),
      shareReplay(1),
    );
    return this.checkAccessInFlight;
  }

  private blockedFallbackState(): VaultAccountBlockedState {
    if (typeof sessionStorage !== 'undefined') {
      const raw = sessionStorage.getItem(VAULT_LOGIN_BLOCKED_MESSAGE_KEY);
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as { subtitle?: unknown };
          if (typeof parsed.subtitle === 'string' && parsed.subtitle.trim()) {
            return { message: parsed.subtitle.trim(), reason: 'unknown' };
          }
        } catch {
          /* ignore */
        }
      }
    }
    return {
      message: buildVaultAccountBlockedUserMessage('unknown'),
      reason: 'unknown',
    };
  }
}

export function vaultAccountBlockedFromHttp(err: HttpErrorResponse): VaultAccountBlockedState | null {
  return resolveVaultAccountBlockedState(err);
}
